import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import FormStepper from 'src/components/FormStepper/FormStepper';
import DialogTransition from 'src/components/DialogTransition';
import { selectTransactionBalance } from 'src/store/selectors/transactionSelectors';
import {
  CLEAR_CONTRACT_TRANSACTIONS,
  loadContractTransaction
} from 'src/store/actions/transactionActions';
import CurrentStep from './CurrentStep';
import makeInitialState from './initialState';

const useStyles = makeStyles()(theme => ({
  title: {
    color: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    '& h2': {
      fontWeight: 400,
      fontSize: theme.spacing(4.25),
      lineHeight: `${theme.spacing(6.5)}`
    }
  },
  root: {
    minWidth: theme.spacing(80)
  },
  closeIcon: {
    float: 'right',
    marginTop: '-25px',
    width: '20px',
    height: '20px'
  }
}));

const CedeContractForm = ({ open, contract, handleClose, title, onSubmit }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const balance = useSelector(selectTransactionBalance);
  const initialState = makeInitialState(contract, balance);
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialState);

  useEffect(() => {
    setData(makeInitialState(contract, balance));
  }, [balance, contract]);

  const stepperLabels = [
    'CONTRACT_VIEW.CEDE_CONTRACT_FORM.STEP_ONE_TITLE',
    'CONTRACT_VIEW.CEDE_CONTRACT_FORM.STEP_TWO_TITLE',
    'CONTRACT_VIEW.CEDE_CONTRACT_FORM.STEP_THREE_TITLE',
    ...(balance !== 0
      ? ['CONTRACT_VIEW.CEDE_CONTRACT_FORM.STEP_FOUR_TITLE']
      : [])
  ];

  const resetRef = useRef();

  useEffect(() => {
    if (open) {
      setStep(0);
      setData(makeInitialState(contract, balance));
      dispatch(loadContractTransaction(contract.id, { page: 0 }));
    }
    return () => {
      if (!open) {
        dispatch({ type: CLEAR_CONTRACT_TRANSACTIONS });
      }
    };
  }, [dispatch, contract.id, contract, balance, open]);

  const handleNext = useCallback(
    values => {
      setData({ ...data, ...values });
      setStep(step + 1);
    },
    [setStep, step, setData, data]
  );

  const handleCloseModal = useCallback(() => {
    if (resetRef.current) {
      resetRef.current();
    }
    setData(initialState);
    setStep(0);
    handleClose();
  }, [handleClose, initialState]);

  const handleBackOrClose = useCallback(() => {
    if (step === 0) {
      if (resetRef.current) {
        resetRef.current();
      }
      setData(initialState);
      setStep(0);
      handleClose();
    } else {
      setData({ ...data });
      setStep(step - 1);
    }
  }, [dispatch, step, setStep, handleClose, resetRef]);

  const handleSubmit = useCallback(
    value => {
      const formData = { ...data, ...value };
      onSubmit(formData, () => setData(initialState));
    },
    [data, onSubmit, initialState]
  );

  if (!open) return null;

  return (
    <div>
      <Dialog
        aria-labelledby="form-new-gps-dialog"
        classes={{ paper: classes.root }}
        open={open}
        scroll="body"
        TransitionComponent={DialogTransition}
        keepMounted
        disableEscapeKeyDown
      >
        <DialogTitle className={classes.title} id="form-new-gps-title">
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={handleCloseModal}
            className={classes.closeIcon}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.title} color="primary" variant="h2">
            {title}
          </Typography>
        </DialogTitle>
        <Divider light variant="middle" />
        <DialogContent>
          <FormStepper index={step} labels={stepperLabels} />
          <CurrentStep
            open={open}
            contract={contract}
            resetRef={resetRef}
            activeStep={step}
            data={data}
            onNext={handleNext}
            onBack={handleBackOrClose}
            handleClose={handleCloseModal}
            onSubmit={handleSubmit}
            setData={setData}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
CedeContractForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  contract: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired
};

export default CedeContractForm;
